import { useEffect, useState, useCallback } from 'react';
import { useApi } from '../../../utils/hooks';
import { SortDirection } from '../../../utils/constants/enums';
import {
  IPagedResult,
  IInvoiceReportDto,
  IInvoiceSearchRequest,
  ISystemCodeByTypeDto,
  IMembershipTypeLookupDto
} from '../../../utils/constants/interfaces';
import { ReportColumn, SearchCriterion } from '../../../components/ui/ReportActions';
import { useReportFormatters } from '../../../utils/hooks/useReportFormatters';
import { getCurrentBillingYear } from '../../../utils/helpers/billingYear';

const emptySearchResults: IPagedResult<IInvoiceReportDto[]> = {
  currentPage: 1,
  results: [],
  pageSize: 1,
  pageCount: 1,
  resultCount: 0
};

const useInvoiceReport = () => {
  const formatters = useReportFormatters();

  const DEFAULT_MEMBER_STATUS_IDS = [21, 50];

  const defaultSearchParams: IInvoiceSearchRequest = {
    // C# InvoiceReportRequest properties
    billingYear: getCurrentBillingYear(),
    balanceGreaterThanOrEqual: null,
    balanceLessThanOrEqual: null,
    memberStatusIds: DEFAULT_MEMBER_STATUS_IDS,
    membershipTypeIds: [],
    // Pagination and sorting
    sortDirection: SortDirection.ASCENDING,
    sortField: 'clubMemberId', // Default sort by Club Member ID ascending
    pageSize: 50,
    page: 1
  };

  const api = useApi();
  const [loading, setLoading] = useState<boolean>(false);
  const [allResults, setAllResults] = useState<IInvoiceReportDto[]>([]);
  const [invoicePagedResults, setInvoicePagedResults] =
    useState<IPagedResult<IInvoiceReportDto[]>>(emptySearchResults);
  const [searchParameters, setSearchParameters] =
    useState<IInvoiceSearchRequest>(defaultSearchParams);
  const [hasUserTriggeredSearch, setHasUserTriggeredSearch] = useState<boolean>(false);
  const [hasPerformedSearch, setHasPerformedSearch] = useState<boolean>(false);

  // Function to apply client-side sorting and pagination
  const applyClientSidePaginationAndSort = useCallback(() => {
    if (allResults.length === 0) {
      setInvoicePagedResults(emptySearchResults);
      return;
    }

    // Apply sorting
    const sortedResults = [...allResults].sort((a, b) => {
      const field = searchParameters.sortField;
      const currentSortDirection: SortDirection = searchParameters.sortDirection;
      let aValue: string | number | boolean | Date | null = a[field as keyof IInvoiceReportDto];
      let bValue: string | number | boolean | Date | null = b[field as keyof IInvoiceReportDto];

      // Convert to strings for comparison if needed
      if (typeof aValue === 'string' && typeof bValue === 'string') {
        aValue = aValue.toLowerCase();
        bValue = bValue.toLowerCase();
      }

      // Handle null values for comparison
      if (aValue === null && bValue === null) return 0;
      if (aValue === null) return currentSortDirection === SortDirection.ASCENDING ? -1 : 1;
      if (bValue === null) return currentSortDirection === SortDirection.ASCENDING ? 1 : -1;

      if (aValue < bValue) {
        return currentSortDirection === SortDirection.ASCENDING ? -1 : 1;
      }
      if (aValue > bValue) {
        return currentSortDirection === SortDirection.ASCENDING ? 1 : -1;
      }
      return 0;
    });

    // Apply pagination
    const totalResults = sortedResults.length;
    const pageCount = Math.ceil(totalResults / searchParameters.pageSize);
    const startIndex = (searchParameters.page - 1) * searchParameters.pageSize;
    const endIndex = startIndex + searchParameters.pageSize;
    const pageResults = sortedResults.slice(startIndex, endIndex);

    const pagedResult: IPagedResult<IInvoiceReportDto[]> = {
      currentPage: searchParameters.page,
      results: pageResults,
      pageSize: searchParameters.pageSize,
      pageCount: pageCount,
      resultCount: totalResults
    };

    setInvoicePagedResults(pagedResult);
  }, [
    allResults,
    searchParameters.sortField,
    searchParameters.sortDirection,
    searchParameters.page,
    searchParameters.pageSize
  ]);

  // Options for dropdowns
  const [memberStatusOptions, setMemberStatusOptions] = useState<ISystemCodeByTypeDto[]>([]);
  const [membershipTypeOptions, setMembershipTypeOptions] = useState<IMembershipTypeLookupDto[]>(
    []
  );
  const [loadingOptions, setLoadingOptions] = useState<boolean>(false);

  // Load dropdown options on component mount
  useEffect(() => {
    const loadOptions = async () => {
      setLoadingOptions(true);
      try {
        // Load member status options
        const memberStatusResponse = await api.get<ISystemCodeByTypeDto[]>(
          '/codes/code-types-by-type/MEMBER_STATUS'
        );
        if (memberStatusResponse?.status?.statusCode === 200 && memberStatusResponse.response) {
          setMemberStatusOptions(memberStatusResponse.response);
        }

        // Load membership type options
        const membershipTypeResponse = await api.get<IMembershipTypeLookupDto[]>(
          '/membership-types/lookup'
        );
        if (membershipTypeResponse?.status?.statusCode === 200 && membershipTypeResponse.response) {
          setMembershipTypeOptions(membershipTypeResponse.response);
        }
      } finally {
        setLoadingOptions(false);
      }
    };

    void loadOptions();
  }, [api]);

  // Apply pagination and sorting whenever allResults or pagination/sort parameters change
  useEffect(() => {
    applyClientSidePaginationAndSort();
  }, [applyClientSidePaginationAndSort]);

  const changePage = (page: number) => {
    if (page === searchParameters.page) {
      return;
    }

    setSearchParameters({ ...searchParameters, page: page });
  };

  const changeSort = (sortField: string) => {
    const currentSortDirection: SortDirection = searchParameters.sortDirection;
    const sortDirection =
      searchParameters.sortField === sortField
        ? currentSortDirection === SortDirection.ASCENDING
          ? SortDirection.DESCENDING
          : SortDirection.ASCENDING
        : SortDirection.ASCENDING;
    setSearchParameters({
      ...searchParameters,
      sortField: sortField,
      sortDirection: sortDirection,
      page: 1 // Reset to page 1 when sorting
    });
  };

  const resetSearch = () => {
    const localSearchParams = { ...defaultSearchParams };
    setSearchParameters(localSearchParams);
    setInvoicePagedResults(emptySearchResults);
    setAllResults([]);
    setHasUserTriggeredSearch(false);
    setHasPerformedSearch(false);
  };

  const triggerSearch = () => {
    const localSearchParams = { ...searchParameters };
    localSearchParams.page = 1;
    setSearchParameters(localSearchParams);
    setHasUserTriggeredSearch(true);
  };

  const searchInvoices = useCallback(async () => {
    setLoading(true);
    setHasUserTriggeredSearch(false); // Reset the flag
    setHasPerformedSearch(true); // Mark that a search has been performed

    try {
      const endpoint = '/members/invoices';

      // Build the request body to match C# AnnualInvoiceReportRequest exactly (PascalCase)
      const requestBody: {
        MemberStatus: number[];
        MemberTypes: number[];
        BillingYear?: number;
        BalanceGreaterThan?: number;
        BalanceLessThan?: number;
      } = {
        MemberStatus: searchParameters.memberStatusIds,
        MemberTypes: searchParameters.membershipTypeIds
      };

      // Only include nullable properties if they have values
      if (searchParameters.billingYear !== null) {
        requestBody.BillingYear = searchParameters.billingYear;
      }
      if (searchParameters.balanceGreaterThanOrEqual !== null) {
        const parsed = parseFloat(searchParameters.balanceGreaterThanOrEqual);
        if (!isNaN(parsed)) {
          requestBody.BalanceGreaterThan = parsed;
        }
      }
      if (searchParameters.balanceLessThanOrEqual !== null) {
        const parsed = parseFloat(searchParameters.balanceLessThanOrEqual);
        if (!isNaN(parsed)) {
          requestBody.BalanceLessThan = parsed;
        }
      }

      const apiResponse = await api.post<IInvoiceReportDto[]>(
        endpoint,
        JSON.stringify(requestBody)
      );

      if (apiResponse?.status?.statusCode === 200 && apiResponse.response) {
        // Store all results for client-side pagination
        setAllResults(apiResponse.response);
      } else {
        setAllResults([]);
        setInvoicePagedResults(emptySearchResults);
      }
    } catch (error) {
      setAllResults([]);
      setInvoicePagedResults(emptySearchResults);
    } finally {
      setLoading(false);
    }
  }, [
    api,
    searchParameters.billingYear,
    searchParameters.balanceGreaterThanOrEqual,
    searchParameters.balanceLessThanOrEqual,
    searchParameters.memberStatusIds,
    searchParameters.membershipTypeIds,
    hasUserTriggeredSearch
  ]);

  // Trigger search only when user clicks search button
  useEffect(() => {
    if (hasUserTriggeredSearch) {
      void searchInvoices();
    }
  }, [searchInvoices]);

  const updateFilter = (field: string, value: string | number[]) => {
    const localSearchParams = { ...searchParameters };
    localSearchParams.page = 1; // Reset to page 1 on new search

    // Handle different field types
    if (field === 'billingYear') {
      localSearchParams.billingYear = value ? parseInt(value as string) : null;
    } else if (field === 'balanceGreaterThanOrEqual') {
      // Store as string to preserve partial decimal inputs like "0." or "0.0"
      const strValue = value as string;
      localSearchParams.balanceGreaterThanOrEqual = strValue === '' ? null : strValue;
    } else if (field === 'balanceLessThanOrEqual') {
      // Store as string to preserve partial decimal inputs like "0." or "0.0"
      const strValue = value as string;
      localSearchParams.balanceLessThanOrEqual = strValue === '' ? null : strValue;
    } else if (field === 'memberStatusIds') {
      localSearchParams.memberStatusIds = value as number[];
    } else if (field === 'membershipTypeIds') {
      localSearchParams.membershipTypeIds = value as number[];
    }

    setSearchParameters(localSearchParams);
  };

  const hasSearchParametersCheck = () => {
    return (
      searchParameters.billingYear !== null ||
      searchParameters.balanceGreaterThanOrEqual !== null ||
      searchParameters.balanceLessThanOrEqual !== null ||
      searchParameters.memberStatusIds.length > 0 ||
      searchParameters.membershipTypeIds.length > 0
    );
  };

  // Define column configuration for exports
  // Column headers match Word mail merge template field names (using underscores)
  const getReportColumns = (): ReportColumn<IInvoiceReportDto>[] => [
    // Invoice/Member identification
    { key: 'id', header: 'AI_ID' },
    { key: 'clubMemberId', header: 'CM_ID' },
    // FM_ID and MC_ID don't exist in new data model - output empty for compatibility
    { key: 'clubMemberId', header: 'FM_ID', formatter: () => '' },
    { key: 'clubMemberId', header: 'MC_ID', formatter: () => '' },

    // Member name fields (underscores to match Word template)
    { key: 'lastName', header: 'Last_Name' },
    { key: 'firstName', header: 'First_Name' },
    {
      key: 'suffix',
      header: 'Suffix',
      formatter: (value) => formatters.formatSuffix(value as number | null)
    },
    {
      key: 'title',
      header: 'Title',
      formatter: (value) => formatters.formatTitle(value as number | null)
    },
    { key: 'memberNotes', header: 'CM_Notes' },

    // Payment fields
    {
      key: 'amountPaidForBillingYear',
      header: 'Amount_Paid',
      formatter: (value) => formatters.formatCurrency(value as number | null)
    },
    { key: 'paymentMethods', header: 'Payment_Methods' },
    { key: 'paymentDates', header: 'Payment_Dates' },
    {
      key: 'balanceDue',
      header: 'Balance_Due',
      formatter: (value) => formatters.formatCurrency(value as number | null)
    },

    // Invoice fields
    { key: 'billingYear', header: 'Billing_Year' },
    {
      key: 'dues',
      header: 'Dues',
      formatter: (value) => formatters.formatCurrency(value as number | null)
    },
    {
      key: 'initiationFee',
      header: 'Initiation_Fee',
      formatter: (value) => formatters.formatCurrency(value as number | null)
    },
    {
      key: 'lateFee',
      header: 'Late_Fee',
      formatter: (value) => formatters.formatCurrency(value as number | null)
    },
    {
      key: 'otherFee',
      header: 'Other_Fee',
      formatter: (value) => formatters.formatCurrency(value as number | null)
    },
    {
      key: 'subTotal',
      header: 'Sub_Total',
      formatter: (value) => formatters.formatCurrency(value as number | null)
    },
    { key: 'notes', header: 'INV_Notes' },
    { key: 'workHours', header: 'Work_Hours' },

    // Dates
    {
      key: 'firstJoinedDateUtc',
      header: 'First_Joined_Date',
      formatter: (value) => formatters.formatDate(value as Date | null)
    },
    {
      key: 'joinedDateUtc',
      header: 'Joined_Date',
      formatter: (value) => formatters.formatDate(value as Date | null)
    },
    {
      key: 'newThisYear',
      header: 'New_this_year',
      formatter: (value) => (value ? 'Y' : '')
    },

    // Member status and type
    { key: 'memberStatus', header: 'Member_Status' },
    { key: 'memberTypeName', header: 'Member_Type' },
    {
      key: 'memberTypeAmount',
      header: 'Full_Rate_for_Member_Type',
      formatter: (value) => formatters.formatCurrency(value as number | null)
    },
    {
      key: 'discountApplied',
      header: 'Discount',
      formatter: (value) => `${(value as number).toFixed(2)}%`
    },
    { key: 'discountReason', header: 'Discount_Basis' },

    // Contact fields
    { key: 'email', header: 'Email' },
    { key: 'address', header: 'Address' },
    { key: 'address2', header: 'Address2' },
    { key: 'city', header: 'City' },
    { key: 'state', header: 'State' },
    { key: 'zip', header: 'Zip' },

    // Alternate address fields (don't exist in new model - output empty for compatibility)
    { key: 'address', header: 'AltAddress', formatter: () => '' },
    { key: 'address', header: 'AltAddress2', formatter: () => '' },
    { key: 'address', header: 'AltCity', formatter: () => '' },
    { key: 'address', header: 'AltState', formatter: () => '' },
    { key: 'address', header: 'AltZip', formatter: () => '' },

    // Audit fields
    { key: 'createdBy', header: 'Created_By' },
    {
      key: 'dateCreatedUtc',
      header: 'Created_Date',
      formatter: (value) => formatters.formatDate(value as Date | null)
    },
    { key: 'lastModifiedBy', header: 'Updated_By' },
    {
      key: 'lastModifiedDateUtc',
      header: 'Updated_Date',
      formatter: (value) => formatters.formatDate(value as Date | null)
    }
  ];

  // Get results sorted for export (matches Java app: Last Name, then First Name)
  const getSortedResultsForExport = (): IInvoiceReportDto[] => {
    return [...allResults].sort((a, b) => {
      // Primary sort: Last Name (ascending)
      const lastNameCompare = (a.lastName || '')
        .toLowerCase()
        .localeCompare((b.lastName || '').toLowerCase());
      if (lastNameCompare !== 0) {
        return lastNameCompare;
      }

      // Secondary sort: First Name (ascending)
      return (a.firstName || '').toLowerCase().localeCompare((b.firstName || '').toLowerCase());
    });
  };

  // Build search criteria for print display
  const getSearchCriteria = (): SearchCriterion[] => {
    const criteria: SearchCriterion[] = [];

    if (searchParameters.billingYear !== null) {
      criteria.push({ label: 'Billing Year', value: searchParameters.billingYear.toString() });
    }

    if (searchParameters.balanceGreaterThanOrEqual !== null) {
      const parsed = parseFloat(searchParameters.balanceGreaterThanOrEqual);
      if (!isNaN(parsed)) {
        criteria.push({
          label: 'Balance >=',
          value: formatters.formatCurrency(parsed)
        });
      }
    }

    if (searchParameters.balanceLessThanOrEqual !== null) {
      const parsed = parseFloat(searchParameters.balanceLessThanOrEqual);
      if (!isNaN(parsed)) {
        criteria.push({
          label: 'Balance <=',
          value: formatters.formatCurrency(parsed)
        });
      }
    }

    if (searchParameters.memberStatusIds.length > 0) {
      const memberStatusNames = searchParameters.memberStatusIds
        .map(
          (id) => memberStatusOptions.find((option) => option.id === id)?.description || 'Unknown'
        )
        .join(', ');
      criteria.push({ label: 'Member Status', value: memberStatusNames });
    }

    if (searchParameters.membershipTypeIds.length > 0) {
      const membershipTypeNames = searchParameters.membershipTypeIds
        .map((id) => membershipTypeOptions.find((option) => option.id === id)?.name || 'Unknown')
        .join(', ');
      criteria.push({ label: 'Membership Type', value: membershipTypeNames });
    }

    return criteria;
  };

  return {
    loading,
    searchParameters,
    invoicePagedResults,
    allResults, // Full dataset for display
    getSortedResultsForExport, // Sorted dataset for CSV/Print export (matches Java app sort order)
    updateFilter,
    hasSearchParameters: hasSearchParametersCheck(),
    hasPerformedSearch,
    changePage,
    changeSort,
    setSearchParameters,
    resetSearch,
    triggerSearch,
    getReportColumns, // Export column configuration
    getSearchCriteria, // Export search criteria for print
    memberStatusOptions,
    membershipTypeOptions,
    loadingOptions
  };
};

export default useInvoiceReport;
